import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, Image, FileText, FileSpreadsheet, Camera, AlertCircle } from 'lucide-react';
import Button from './Button';

const FileUpload = ({
  onFilesSelected,
  onUpload,
  maxFiles = 10,
  maxFileSize = 50,
  allowedTypes = [
    'image/jpeg',
    'image/png', 
    'image/heic',
    'application/pdf',
    'text/csv',
    'text/plain',
    'application/octet-stream', // for IGC files
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ],
  disabled = false,
  showPreview = true,
  acceptMobileCapture = true,
  className = '',
  compact = false
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // File type validation and display helpers
  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <Image className="w-5 h-5" />;
    if (file.type === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (file.type.includes('spreadsheet') || file.type.includes('excel') || file.name.endsWith('.csv')) 
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    if (file.name.endsWith('.igc')) return <File className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5" />;
  };

  const getFileExtension = (filename) => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isValidFileType = (file) => {
    const extension = getFileExtension(file.name);
    
    // Special handling for IGC files
    if (extension === 'igc') {
      return true;
    }
    
    return allowedTypes.includes(file.type);
  };

  const isValidFileSize = (file) => {
    return file.size <= maxFileSize * 1024 * 1024;
  };

  const validateFiles = (files) => {
    const errors = [];
    const valid = [];

    for (const file of files) {
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: File type not allowed`);
        continue;
      }
      
      if (!isValidFileSize(file)) {
        errors.push(`${file.name}: File too large (max ${maxFileSize}MB)`);
        continue;
      }
      
      valid.push(file);
    }

    if (selectedFiles.length + valid.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`);
      return { valid: valid.slice(0, maxFiles - selectedFiles.length), errors };
    }

    return { valid, errors };
  };

  const createFilePreview = useCallback((file) => {
    const fileWithPreview = Object.assign(file, { 
      id: `${Date.now()}-${Math.random()}`,
      preview: undefined 
    });

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        fileWithPreview.preview = e.target?.result;
        setSelectedFiles(prev => [...prev]);
      };
      reader.readAsDataURL(file);
    }

    return fileWithPreview;
  }, []);

  const handleFileSelect = (files) => {
    if (!files) return;
    
    const filesArray = Array.from(files);
    const { valid, errors } = validateFiles(filesArray);
    
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }
    
    setError(null);
    const newFiles = valid.map(createFilePreview);
    const updatedFiles = [...selectedFiles, ...newFiles];
    
    setSelectedFiles(updatedFiles);
    onFilesSelected(updatedFiles);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    handleFileSelect(e.dataTransfer.files);
  }, [disabled, selectedFiles, onFilesSelected]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  const removeFile = (fileId) => {
    const updatedFiles = selectedFiles.filter(f => f.id !== fileId);
    setSelectedFiles(updatedFiles);
    onFilesSelected(updatedFiles);
  };

  const handleUpload = async () => {
    if (!onUpload || selectedFiles.length === 0) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      await onUpload(selectedFiles);
      setSelectedFiles([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // Build accept string for input
  const acceptString = [
    ...allowedTypes,
    '.jpg,.jpeg,.png,.heic,.pdf,.csv,.txt,.igc,.doc,.docx,.xls,.xlsx'
  ].join(',');

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Drop Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg text-center transition-colors
          ${compact ? 'p-4' : 'p-8'}
          ${isDragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={!disabled ? openFileDialog : undefined}
      >
        <div className={compact ? 'space-y-2' : 'space-y-4'}>
          <Upload className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} text-gray-400 mx-auto`} />
          <div>
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-medium text-gray-900`}>
              Drop files here or click to browse
            </p>
            {!compact && (
              <p className="text-sm text-gray-500 mt-2">
                Supports: Images (JPG, PNG, HEIC), IGC files, PDF, CSV, TXT, Word, Excel
              </p>
            )}
          </div>
          
          {/* Mobile Camera Button */}
          {acceptMobileCapture && (
            <div className="md:hidden">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.capture = 'environment';
                  input.multiple = maxFiles > 1;
                  input.onchange = (e) => {
                    handleFileSelect(e.target.files);
                  };
                  input.click();
                }}
                className="inline-flex items-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Take Photo
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={acceptString}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
        {...(acceptMobileCapture && { capture: 'environment' })}
      />

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Selected Files Preview */}
      {showPreview && selectedFiles.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Selected Files ({selectedFiles.length})</h4>
          <div className="space-y-2">
            {selectedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-md"
              >
                {/* File Preview/Icon */}
                <div className="flex-shrink-0">
                  {file.preview ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                      {getFileIcon(file)}
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                  disabled={disabled || isUploading}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Upload Button */}
          {onUpload && selectedFiles.length > 0 && (
            <div className="flex justify-end">
              <Button
                onClick={handleUpload}
                disabled={disabled || isUploading}
                className="inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} file(s)`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
